import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  EModalSize,
  EModalVariants,
  ETextStyleVariant,
  Heading,
  Modal,
  Text,
} from '@outdoorsyco/bonfire';
import { useEffect, useState } from 'react';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { getItemFromLocalStorage, setItemInLocalStorage } from '@/utility/localstorage';

import WishlistsVideo from './WishlistsVideo';

interface WishlistsWelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onButtonClick: () => void;
  headerText: string;
  videoSrc: string;
  contentTitle: string;
  contentText: string;
  buttonLabel: string;
  imageFallbackSrc?: string;
  size?: EModalSize;
}

export const WishlistsWelcomeModal = ({
  isOpen,
  onClose,
  onButtonClick,
  headerText,
  videoSrc,
  contentTitle,
  contentText,
  buttonLabel,
  imageFallbackSrc,
  size = EModalSize.Large,
}: WishlistsWelcomeModalProps) => {
  const { isMobile } = useBreakpoint();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      variant={isMobile ? EModalVariants.Bottom : undefined}>
      {isMobile && (
        <Modal.Actions className="[&>span]:bg-[#F1F2F3] [&>span>button]:text-black [&>span]:rounded-full [&>span>button]:w-10 [&>span>button]:h-10" />
      )}

      {!isMobile && (
        <Modal.Header className="flex justify-center pt-12">
          <Heading variant={EHeadingStyleVariant.H3}>{headerText}</Heading>
        </Modal.Header>
      )}

      <Modal.Content className={`flex flex-col ${isMobile ? 'pb-12' : 'px-4 py-6 max-h-[70vh]'}`}>
        <div className="flex flex-col items-center">
          <WishlistsVideo
            videoSrc={videoSrc}
            imageFallbackSrc={imageFallbackSrc}
            isMobile={isMobile}
          />

          {isMobile && (
            <Heading variant={EHeadingStyleVariant.H4} className="pt-12 pb-2">
              {contentTitle}
            </Heading>
          )}

          <Text
            variant={ETextStyleVariant.MediumRegular}
            className={!isMobile ? 'mb-6 text-center' : undefined}>
            {contentText}
          </Text>
        </div>
      </Modal.Content>

      <Modal.Footer>
        <div className="flex justify-center w-full">
          <Button
            variant={EButtonColorVariant.Primary}
            label={buttonLabel}
            onClick={onButtonClick}
            className={isMobile ? 'w-full' : 'w-[427px]'}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
};

// Hook that manages the welcome modal state and handles the one-time-only display logic
export const useWishlistsWelcomeModal = ({ modalKey }: { modalKey: string }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const hasUserSeenModalBefore = getItemFromLocalStorage(modalKey);

    if (!hasUserSeenModalBefore) {
      setIsModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
    setItemInLocalStorage(modalKey, 'true');
  };

  return {
    isModalOpen,
    closeModal,
  };
};
